import {Trans, Plural, plural, t} from '@lingui/macro'
import {AnimatePresence, motion} from 'framer-motion'
import React from 'react'
import {useNavigate} from 'react-router-dom'
import styled from 'styled-components'

import {
    Text,
    Button,
    Inline,
    Stack,
    LoadingOverlay,
    tokens,
    Link,
    Callout,
} from '@pleo-io/telescope'

import config from 'config'
import background from 'images/bgn-login.png'
import googleLogo from 'images/google-logo.svg'
import microsoftLogo from 'images/microsoft-logo.svg'
import incorrectPinImage from 'images/pin/incorrect.png'
import AuthAppIllustration from 'images/sca/auth-app-bird.svg'
import EmailValidationIllustration from 'images/sca/email-validation-pigeon.svg'
import ExpiredIllustration from 'images/sca/expired-bird.svg'
import PasscodeIllustration from 'images/sca/passcode-bird.svg'
import VerifyIllustration from 'images/sca/verify-bird.svg'
import {isRequestError} from '~/api'
import {OtpType} from '~/api/auth/register'
import {CodeInput} from '~/auth/code-input'
import {checkEmail, OTP_EXPIRATION_TIME, requestOtp} from '~/auth/otp/api'
import {useTimer} from '~/auth/otp/timer/use-timer'
import {getErrorMessage} from '~/error/message'
import {reportError} from '~/error/report'
import {LoginScreen, useLoginLocationState} from '~/feature-sca/login/login.helpers'
import {PasscodeInput} from '~/feature-sca/passcode-input'
import {
    convertThrottledToMinutes,
    ErrorMessageType,
    getTimeThrottled,
    RESET_ERROR_DELAY,
    useOtpNotifications,
    useOtpResendTimer,
} from '~/feature-sca/sca'
import {ContactSupport} from '~/feature-ui/contact-support'
import {useFlags} from '~/flags'
import {PleoLogo} from '~/generic-ui/logo-pleo/logo-pleo'
import {ColumnImage} from '~/generic-ui/page-legacy/composition.styles'
import {useAppLanguage} from '~/i18n'
import {getHelpCentreArticleLink} from '~/locale/helpers'
import {ExternalProvider, useOauthUrl} from '~/shell/oauth'
import {useLoginAttemptChallenge, useVerifiedLogin} from '~/shell/session'
import {useToaster} from '~/shell/toaster'
import tracking from '~/tracking'
import {useDebouncePromise} from '~/use-debounce-promise'

import {EmailInput} from './email-input'

import * as s from '../../libs/otp.styles'

const commercialWebUrl = config.endpoints.commercial

const DEBOUNCE_OTP_REQUEST_MS = 1000

export function EmailScreen() {
    const {state: locationState, update, clear} = useLoginLocationState()
    const [isLoading, setIsLoading] = React.useState(false)
    const [hasError, setHasError] = React.useState(false)
    const [accountLockedTimeout, setAccountLockedTimeout] = React.useState(0)
    const flags = useFlags()
    const {showToast} = useToaster()
    const appLanguage = useAppLanguage()
    const requestOtpDebounced = useDebouncePromise(requestOtp, DEBOUNCE_OTP_REQUEST_MS, {
        leading: true,
    })

    React.useEffect(() => {
        if (flags.webPasscodeReset) {
            // We want to show account locked timeout callout when necessary, but
            // we should hide it on any state update or even on page refresh
            // thus we have to immediately clean this state property and instead store it
            // in the local component's state

            if (locationState.accountLockedTimeout) {
                setAccountLockedTimeout(locationState.accountLockedTimeout)
            }

            update(
                {
                    ...locationState,
                    // always clean up account locked state
                    accountLockedTimeout: undefined,
                },
                true,
            )
        }
    }, [])

    const navigateToNextStep = ({email, ssoAuthUrl}: {email: string; ssoAuthUrl?: string}) => {
        if (ssoAuthUrl) {
            window.location.href = ssoAuthUrl
        } else {
            update({
                email,
                screen: LoginScreen.PASSCODE,
            })
        }
    }

    // If SSO with google we navigate to the validation screen
    React.useEffect(() => {
        const email = locationState?.email
        const loginToken = locationState?.loginToken
        if (!loginToken || !email || isLoading || hasError) {
            return
        }

        const requestOtpAndNavigateToNextStep = async () => {
            try {
                // We set this isLoading state to avoid requesting the otp more than once.
                // There's no need to update the isLoading state after this request is resolved since the user is already on the next screen
                setIsLoading(true)
                const response = await requestOtpDebounced({
                    email,
                    loginToken,
                    language: appLanguage,
                })
                update({
                    ...response,
                    email: locationState.email,
                    screen: LoginScreen.OTP_EMAIL_VALIDATION,
                })
            } catch (error) {
                const message = getErrorMessage(error)
                setHasError(!!message)
                setIsLoading(false)
                update({
                    email: null,
                    screen: LoginScreen.EMAIL,
                })
                reportError(error)
            }
        }

        requestOtpAndNavigateToNextStep()
    }, [locationState.loginToken, locationState.email, update, isLoading, hasError, clear])

    const onSubmitEmail = async (email: string) => {
        setIsLoading(true)
        try {
            const response = await checkEmail(email)
            setIsLoading(false)
            navigateToNextStep({...response, email})
        } catch (error) {
            setIsLoading(false)
            showToast(t`Please try again`, {
                title: t`Something went wrong.`,
                level: 'error',
            })
            update({email: null})
            reportError(error)
        }
    }

    const googleUrl = useOauthUrl({
        action: 'external',
        provider: ExternalProvider.GOOGLE,
        scope: ['resource:basic'],
        register: false,
    })

    const microsoftUrl = useOauthUrl({
        action: 'external',
        provider: ExternalProvider.MICROSOFT,
        scope: ['resource:basic'],
        register: false,
    })

    if (!googleUrl) {
        return null
    }

    if (locationState.loginToken && !hasError) {
        return <LoadingOverlay data-testid="loading-indicator" />
    }

    return (
        <s.Wrapper>
            <s.Content>
                <s.Logo>
                    <PleoLogo />
                </s.Logo>
                <s.Form>
                    <section>
                        <Stack space={18} stretch>
                            <Stack space={32} stretch>
                                <Text variant="3xlarge-accent">
                                    <Trans>Log in to Pleo</Trans>
                                </Text>
                                <Stack space={20} stretch>
                                    <OauthButton href={googleUrl}>
                                        <img src={googleLogo} alt={t`Google logo`} />
                                        <Trans>Continue with Google</Trans>
                                    </OauthButton>
                                    <OauthButton href={microsoftUrl}>
                                        <img src={microsoftLogo} alt={t`Microsoft logo`} />
                                        <Trans>Continue with Microsoft</Trans>
                                    </OauthButton>
                                </Stack>
                                <DividerWrapper>
                                    <Divider>
                                        <Trans>or</Trans>
                                    </Divider>
                                </DividerWrapper>
                            </Stack>
                            <Stack stretch space={40}>
                                {accountLockedTimeout > 0 && (
                                    <Callout variant="error">
                                        <Callout.Text>
                                            <Trans>
                                                Too many verification code requests. Your account
                                                has been locked for 24 hours for security reasons.
                                                Please contact support@pleo.io to get access to your
                                                account.
                                            </Trans>
                                        </Callout.Text>
                                    </Callout>
                                )}
                                <EmailInput
                                    isLoading={Boolean(locationState.email && isLoading)}
                                    initialValue={locationState.email}
                                    onSubmit={onSubmitEmail}
                                />
                            </Stack>
                        </Stack>
                    </section>
                </s.Form>
                <s.BottomNav>
                    <Trans>
                        No account?{' '}
                        <Button variant="link" href={commercialWebUrl}>
                            Get started for free
                        </Button>
                    </Trans>
                </s.BottomNav>
            </s.Content>
            <ColumnImage $imagePath={background} />
        </s.Wrapper>
    )
}
export function PasscodeScreen() {
    const flags = useFlags()
    const {
        state: {email},
        update,
    } = useLoginLocationState()
    const [isLoading, setIsLoading] = React.useState(false)
    const [hasError, setHasError] = React.useState(false)
    const [canRetry, setCanRetry] = React.useState(true)
    const [timeThrottled, setTimeThrottled] = React.useState(0)
    const minutesThrottled = convertThrottledToMinutes(timeThrottled)
    const notifications = useOtpNotifications()
    const appLanguage = useAppLanguage()
    const requestOtpDebounced = useDebouncePromise(requestOtp, DEBOUNCE_OTP_REQUEST_MS, {
        leading: true,
    })

    const navigateBack = () => {
        setIsLoading(false)
        setHasError(false)
        setCanRetry(true)
        update({
            passcode: null,
            phoneLastDigits: undefined,
            googleAuth: undefined,
            screen: LoginScreen.EMAIL,
        })
    }

    const onSetPasscode = async (passcode: string) => {
        if (!email || !passcode) {
            return
        }
        try {
            setIsLoading(true)
            const response = await requestOtpDebounced({email, passcode, language: appLanguage})

            setIsLoading(false)
            update({
                ...response,
                passcode,
                screen: LoginScreen.OTP_EMAIL_VALIDATION,
            })
        } catch (err) {
            const message = getErrorMessage(err)
            setHasError(!!message)
            setIsLoading(false)
            if (isRequestError(err) && message === ErrorMessageType.throttled) {
                const throttled = getTimeThrottled(err.response)
                setCanRetry(false)
                setTimeThrottled(throttled)
            } else if (message === ErrorMessageType.phone_otp_limit_exceeded) {
                notifications[ErrorMessageType.phone_otp_limit_exceeded]()
                navigateBack()
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    setHasError(false)
                    update({
                        phoneLastDigits: undefined,
                        passcode: null,
                    })
                }, RESET_ERROR_DELAY)
            }
            reportError(err)
        }
    }

    return (
        <s.Wrapper>
            <s.Content>
                <s.Logo />
                <s.Form>
                    <section>
                        <Stack space={40} stretch>
                            {isLoading && <LoadingOverlay data-testid="loading-indicator" />}
                            {!hasError && (
                                <>
                                    <Text variant="3xlarge-accent" align="center">
                                        <Trans>Enter passcode</Trans>
                                    </Text>
                                    <PasscodeInput onSubmit={onSetPasscode} />
                                </>
                            )}
                            {hasError && canRetry && (
                                <>
                                    <Text variant="3xlarge-accent" align="center">
                                        <Trans>Incorrect passcode</Trans>
                                    </Text>
                                    <s.PinIllustration>
                                        <img src={incorrectPinImage} alt={t`Incorrect Pin`} />
                                    </s.PinIllustration>
                                </>
                            )}
                            {hasError && !canRetry && (
                                <>
                                    <Text variant="3xlarge-accent" align="center">
                                        <Trans>Too many wrong attempts.</Trans>
                                    </Text>
                                    <Text variant="2xlarge-accent" align="center">
                                        <Plural
                                            value={minutesThrottled}
                                            _0=""
                                            one="You can try again in # minute"
                                            other="You can try again in # minutes"
                                        />
                                    </Text>
                                    <s.PinIllustration>
                                        <img src={incorrectPinImage} alt={t`Incorrect Pin`} />
                                    </s.PinIllustration>
                                </>
                            )}
                            <Inline alignX="center">
                                {flags.webPasscodeReset ? (
                                    <Link href="/reset/passcode">
                                        <Trans>Forgot passcode?</Trans>
                                    </Link>
                                ) : (
                                    <Link
                                        onClick={() => {
                                            tracking.forgotPasscodeLinkActioned({
                                                action: 'initial_login',
                                            })
                                        }}
                                        href={getHelpCentreArticleLink(
                                            '4322549-if-you-forget-your-passcode-or-need-to-change-your-authentication-method',
                                        )}
                                        target="_blank"
                                    >
                                        <Trans>Forgot passcode?</Trans>
                                    </Link>
                                )}
                            </Inline>
                        </Stack>
                    </section>
                </s.Form>
                <s.BottomNav>
                    <Button variant="link" onClick={navigateBack}>
                        &larr; <Trans>Back</Trans>
                    </Button>
                </s.BottomNav>
            </s.Content>
            <ColumnImage>
                <img src={PasscodeIllustration} alt="" />
            </ColumnImage>
        </s.Wrapper>
    )
}

export function OtpValidationForEmailVerificationScreen() {
    const {
        state: {
            email,
            loginToken,
            passcode,
            phoneLastDigits,
            googleAuth,
            redirect,
            otp: previousOtp,
        },
        update,
    } = useLoginLocationState()
    const {codeChallenge, codeChallengeMethod} = useLoginAttemptChallenge()
    const [isLoading, setIsLoading] = React.useState(false)
    const [isTokenSent, setIsTokenSent] = React.useState(true)
    const [isResendingToken, setIsResendingToken] = React.useState(false)
    const [canResendToken, setCanResendToken] = React.useState(true)
    const [hasError, setHasError] = React.useState(false)
    const [hasTokenSentError, setHasTokenSentError] = React.useState(false)
    const resendTimer = useOtpResendTimer()
    const {showToast} = useToaster()
    const notifications = useOtpNotifications()
    const verifiedLogin = useVerifiedLogin()
    const navigate = useNavigate()
    const appLanguage = useAppLanguage()
    const otpType = phoneLastDigits ? OtpType.PHONE : OtpType.GOOGLE_AUTH
    const requestOtpDebounced = useDebouncePromise(requestOtp, DEBOUNCE_OTP_REQUEST_MS, {
        leading: true,
    })

    // Here we check to ensure that the otp is sent successfully
    const isOtpSent = !hasTokenSentError && isTokenSent

    // When the token is sent we begin a countdown timer.
    // Each token is valid for 10 minutes.
    const timer = useTimer(isOtpSent ? OTP_EXPIRATION_TIME : 0)

    const isTokenVerifying = timer.currentTime !== 0

    const isTokenExpired = isTokenSent && !hasError && timer.currentTime === 0

    const isResendDisabled =
        (timer.elaspedTime === 0 && !hasError) || resendTimer.isResendDisabled || !canResendToken

    const navigateBack = () => {
        if (loginToken) {
            // Resetting this takes use to the email screen
            // At this point we'll have the email prefilled.
            update({screen: LoginScreen.EMAIL})
            return
        }
        // Resetting this takes the user back to the the passcode screen
        update({
            screen: LoginScreen.PASSCODE,
            otp: null,
            passcode: null,
            phoneLastDigits: undefined,
            googleAuth: undefined,
        })
    }

    const resendToken = async () => {
        if (!email || (!passcode && !loginToken) || isResendingToken || !canResendToken) {
            return
        }

        try {
            // Ensure we clear the formerly expired code here on resend
            update({otp: null})
            setIsResendingToken(true)
            setHasError(false)
            resendTimer.handleDisableResend()
            const response = await requestOtpDebounced({
                email,
                loginToken,
                passcode,
                language: appLanguage,
            })
            setIsResendingToken(false)
            setIsTokenSent(true)
            update({
                ...response,
            })
        } catch (err) {
            const message = getErrorMessage(err)
            setHasTokenSentError(!!message)
            setIsLoading(false)
            if (isRequestError(err) && message === ErrorMessageType.throttled) {
                const throttled = getTimeThrottled(err.response)
                setCanResendToken(false)
                showToast(t`Too many requests`, {
                    title: plural(throttled, {
                        one: `Try again in # minute.`,
                        other: `Try again in # minutes.`,
                    }),
                    level: 'error',
                })
            }

            if (message === ErrorMessageType.phone_otp_limit_exceeded) {
                notifications[ErrorMessageType.phone_otp_limit_exceeded]()
                navigateBack()
            }
            reportError(err)
        }
    }

    const onSubmitOtp = async (otp: string) => {
        if (
            !email ||
            !otp ||
            !otpType ||
            !codeChallenge ||
            !codeChallengeMethod ||
            (!passcode && !loginToken)
        ) {
            return
        }

        try {
            setIsLoading(true)
            const response = await verifiedLogin({
                email,
                loginToken: loginToken ?? null,
                passcode: passcode ?? null,
                otp,
                trust: false,
                otpType,
                codeChallenge,
                codeChallengeMethod,
            })
            setIsLoading(false)

            if (response.result === 'authenticated') {
                navigate('/trust-browser', {
                    state: {
                        addAccount: false,
                        redirectTo: redirect,
                    },
                })
            } else {
                update({
                    otp,
                    otpType,
                    screen: LoginScreen.EMAIL_VALIDATION,
                })
            }
        } catch (err) {
            reportError(err)
            showToast(t`Please try again`, {
                title: t`Wrong credentials`,
                level: 'error',
            })
            setIsLoading(false)
        }
    }

    const showTokenValidationLoader = (isResendingToken && !hasError) || isLoading

    return (
        <s.Wrapper>
            <s.Content>
                <s.Logo />
                <s.Form>
                    <section>
                        <Stack space={24} stretch>
                            {showTokenValidationLoader && (
                                <LoadingOverlay data-testid="loading-indicator" />
                            )}
                            {!isTokenExpired && (
                                <>
                                    <Text variant="3xlarge-accent">
                                        <Trans>Enter verification code</Trans>
                                    </Text>
                                    <Text variant="medium-default">
                                        {!googleAuth && (
                                            <>
                                                <Trans>
                                                    Check your phone and enter the 6-digit code
                                                    we've sent to your number ending in{' '}
                                                    {phoneLastDigits}.
                                                </Trans>{' '}
                                                <ContactSupport
                                                    chatLabel={t`New number?`}
                                                    emailLabel={t`New number? Contact support@pleo.io`}
                                                />
                                            </>
                                        )}
                                        {googleAuth && (
                                            <>
                                                <Trans>
                                                    Check your authenticator app and enter the
                                                    6-digit code you see there.
                                                </Trans>{' '}
                                                <Button
                                                    variant="link"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href=" https://support.google.com/accounts/answer/1066447?hl=en&ref_topic=2954345"
                                                >
                                                    <Trans>Learn more</Trans>
                                                </Button>
                                            </>
                                        )}
                                    </Text>
                                    <CodeInput
                                        initialValue={previousOtp}
                                        hasError={hasError}
                                        onSubmit={onSubmitOtp}
                                    />
                                    <Inline space={18} alignY="center" alignX="left">
                                        {hasError && (
                                            <Text variant="medium-default" color="red700">
                                                <Trans>Wrong code, try again</Trans>
                                            </Text>
                                        )}
                                        {!googleAuth && !hasError && (
                                            <>
                                                <Text variant="medium-default">
                                                    <Trans>Code expires in</Trans>{' '}
                                                    {isTokenVerifying && (
                                                        <s.TimerFragmentWrapper>
                                                            {timer.minutes}:{timer.seconds}
                                                        </s.TimerFragmentWrapper>
                                                    )}
                                                </Text>

                                                <Button
                                                    variant="link"
                                                    onClick={resendToken}
                                                    disabled={isResendDisabled}
                                                >
                                                    <Trans>Resend</Trans>
                                                </Button>
                                            </>
                                        )}
                                    </Inline>
                                </>
                            )}
                            {isTokenExpired && (
                                <>
                                    <Text variant="3xlarge-accent">
                                        <Trans>Code expired</Trans>
                                    </Text>
                                    <Text variant="medium-default">
                                        <Trans>
                                            The code we've sent to your phone number ending in{' '}
                                            {phoneLastDigits} expired, get a new one to log in.
                                        </Trans>{' '}
                                        <ContactSupport
                                            chatLabel={t`New number?`}
                                            emailLabel={t`New number? Contact support@pleo.io`}
                                        />
                                    </Text>
                                    <Button variant="primary" onClick={resendToken}>
                                        <Trans>Resend</Trans>
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </section>
                </s.Form>
                <s.BottomNav>
                    <Button variant="link" onClick={navigateBack}>
                        &larr; <Trans>Back</Trans>
                    </Button>
                </s.BottomNav>
            </s.Content>
            <ColumnImage>
                {!isTokenExpired && !googleAuth && <img src={VerifyIllustration} alt="" />}
                {!isTokenExpired && googleAuth && <img src={AuthAppIllustration} alt="" />}
                {isTokenExpired && <img src={ExpiredIllustration} alt="" />}
            </ColumnImage>
        </s.Wrapper>
    )
}

export function EmailValidationScreen() {
    const {
        state: {loginToken, email},
        update,
    } = useLoginLocationState()

    const navigateBack = () => {
        if (loginToken) {
            // Resetting this takes use to the email screen
            // At this point we'll have the email prefilled.
            update({screen: LoginScreen.EMAIL})
            return
        }
        // Resetting this takes the user back to the the passcode screen
        update({
            screen: LoginScreen.PASSCODE,
            otp: null,
            passcode: null,
            phoneLastDigits: undefined,
            googleAuth: undefined,
        })
    }

    return (
        <s.Wrapper>
            <s.Content>
                <s.Logo />
                <s.Form>
                    <section>
                        <Stack space={24} stretch>
                            <Text variant="3xlarge-accent">
                                <Trans>Verify your email</Trans>
                            </Text>
                            <Stack space={8}>
                                <Text variant="medium-default">
                                    <Trans>
                                        We've sent an email to <strong>{email}</strong>. Go to your
                                        inbox on this device and follow the link to continue logging
                                        in.
                                    </Trans>
                                </Text>
                                <Link
                                    href={getHelpCentreArticleLink(
                                        '4774960-how-to-login-to-your-pleo-account-with-strong-customer-authentication',
                                    )}
                                    target="_blank"
                                >
                                    <Trans>Didn't get an email?</Trans>
                                </Link>
                            </Stack>
                        </Stack>
                    </section>
                </s.Form>
                <s.BottomNav>
                    <Button variant="link" onClick={navigateBack}>
                        &larr; <Trans>Back</Trans>
                    </Button>
                </s.BottomNav>
            </s.Content>
            <ColumnImage>
                <img src={EmailValidationIllustration} alt={''} />
            </ColumnImage>
        </s.Wrapper>
    )
}

/**
 * Login Flow
 * This will support both legacy and the new 2FA way of doing things
 * (Account has verified phone number) => (1) Ask for passcode => (2) Request Otp (one time password) => (3) Gain Access
 *
 */
export function LoginFlow() {
    const {
        state: {screen},
    } = useLoginLocationState()

    const element = React.useMemo(() => {
        let el
        switch (screen) {
            case LoginScreen.PASSCODE:
                el = <PasscodeScreen />
                break
            case LoginScreen.OTP_EMAIL_VALIDATION:
                el = <OtpValidationForEmailVerificationScreen />
                break
            case LoginScreen.EMAIL_VALIDATION:
                el = <EmailValidationScreen />
                break
            default:
                el = <EmailScreen />
        }

        return el
    }, [screen])

    return (
        <AnimatePresence initial={false}>
            <motion.div
                key={screen ?? undefined}
                initial={screen ? screenAnimations.initial : undefined}
                animate={screenAnimations.animate}
                exit="initial"
            >
                {element}
            </motion.div>
        </AnimatePresence>
    )
}

const screenAnimations = {
    initial: {
        x: 0,
        opacity: 0,
        transition: {
            opacity: tokens.slowOut,
        },
    },
    animate: {
        x: 0,
        opacity: 1,
        transition: {
            opacity: tokens.smoothIn,
        },
    },
}

export const OauthButton = styled(Button).attrs({variant: 'secondary'})`
    img {
        display: inline-block;
        margin-right: ${tokens.spacing8};
        margin-left: ${tokens.spacing8};
    }
`

export const DividerWrapper = styled.div`
    margin: ${tokens.spacing20} 0;
`

export const Divider = styled(Text).attrs({variant: 'medium-default', as: 'p'})`
    width: 100%;
    display: flex;

    &::before,
    &::after {
        content: '';
        border-bottom: ${tokens.borderPrimary};
        margin: auto;
    }

    &::before {
        flex: 1;
        margin-right: ${tokens.spacing12};
    }

    &::after {
        flex: 7;
        margin-left: ${tokens.spacing12};
    }
`
